import { AbortControllerManager } from '../abortControllerManager';
import { authClient } from '../client';
import { IGetUsersQueryParams, IUserInvite } from '@/pages/AdminPanel/types';
import type {
  IUpdateUserByRoleAndInstitutionRequest,
  IUserData,
} from '@/types';
import { ITemplateResponse } from '@/types/common';
import { convertToQueryParams } from '@/utils/convertToQueryParams';

class UsersApi extends AbortControllerManager {
  private USERS_URL = '/v2/users';
  private USER_INVITE_URL = '/v2/user-invite';

  constructor() {
    super();
  }

  // INVITE USER
  sendInvite(payload: IUserInvite) {
    const signal = this.createAbortController('sendInvite');
    return authClient.post(this.USER_INVITE_URL, payload, { signal });
  }

  // USERS API
  getUsers(
    paramsObj?: IGetUsersQueryParams,
  ): Promise<ITemplateResponse<IUserData[]>> {
    const signal = this.createAbortController(
      `getUsers-${paramsObj?.role ?? ''}-${paramsObj?.institution}`.trim(),
    );
    const params = paramsObj
      ? convertToQueryParams({ ...paramsObj })
      : undefined;
    return authClient.get(
      params ? `${this.USERS_URL}?${params}` : this.USERS_URL,
      {
        signal,
      },
    );
  }

  getUserById(id: string): Promise<ITemplateResponse<IUserData>> {
    const signal = this.createAbortController(`getUserById-${id}`);
    return authClient.get(`${this.USERS_URL}/${id}`, {
      signal,
    });
  }

  updateUserByInstitutionRoles(body: IUpdateUserByRoleAndInstitutionRequest) {
    const url = `${this.USERS_URL}/institution-roles`;
    const signal = this.createAbortController(
      `updateRoles-${body.user_id}-${body.role}-${body.institution_id}`,
    );
    return authClient.post(url, body, { signal });
  }

  setActiveStatus(userId: string, active: boolean) {
    const url = `${this.USERS_URL}/${userId}`;
    const signal = this.createAbortController(`setActiveStatus-${userId}`);
    return authClient.patch(url, { is_active: active }, { signal });
  }

  removeUserRole(roleId: string) {
    const url = `${this.USERS_URL}/roles/${roleId}`;
    const signal = this.createAbortController(`removeUserRole-${roleId}`);
    return authClient.delete(url, { signal });
  }
}

export const usersApi = new UsersApi();
